/**
 * Doc 03 §4 — the menu-level `design_overrides` layer.
 *
 * Resolution order at render: template.variables → theme package → overrides.
 * The merchant-facing surface shows 6 colour slots + 3 font slots; each slot
 * maps to raw template variable keys (via COLOR_ALIASES) so an override
 * recolors the template bindings, not the questionnaire model.
 *
 * This module owns: the slot registry, the curated 24-font picker list, the
 * zod validation contract, and contrast guidance for the guard rails in §4.3.
 */

import { z } from 'zod';

import { fonts } from '@/lib/types';
import { contrastRatio as colordContrastRatio } from './contrast';

export const DESIGN_COLOR_KEYS = [
  'background',
  'surface',
  'primary',
  'text',
  'price',
  'accent',
] as const;

export type DesignColorKey = (typeof DESIGN_COLOR_KEYS)[number];

export const DESIGN_FONT_KEYS = ['heading', 'body', 'price'] as const;

export type DesignFontKey = (typeof DESIGN_FONT_KEYS)[number];

/** What the merchant sees — label + the raw template variable slot it writes. */
export const DESIGN_COLOR_SLOTS: Array<{ key: DesignColorKey; label: string }> = [
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Cards' },
  { key: 'primary', label: 'Headings' },
  { key: 'text', label: 'Item names' },
  { key: 'price', label: 'Prices' },
  { key: 'accent', label: 'Accent' },
];

export const DESIGN_FONT_SLOTS: Array<{ key: DesignFontKey; label: string }> = [
  { key: 'heading', label: 'Heading' },
  { key: 'body', label: 'Body' },
  { key: 'price', label: 'Prices' },
];

export const DESIGN_CONTRAST_PAIRS: Array<{ name: string; fg: DesignColorKey; bg: DesignColorKey }> = [
  { name: 'Item names on Cards', fg: 'text', bg: 'surface' },
  { name: 'Prices on Cards', fg: 'price', bg: 'surface' },
  { name: 'Headings on Background', fg: 'primary', bg: 'background' },
];

export type FontGroup = 'Display' | 'Serif' | 'Sans' | 'Script' | 'Hand';

export interface CuratedFont {
  name: string;
  css: string;
  group: FontGroup;
}

const CURATED: Record<string, { group: FontGroup }> = {
  // Display
  'Bebas Neue': { group: 'Display' },
  Anton: { group: 'Display' },
  Oswald: { group: 'Display' },
  Righteous: { group: 'Display' },
  'Fugaz One': { group: 'Display' },
  'Gravitas One': { group: 'Display' },
  // Serif
  'Playfair Display': { group: 'Serif' },
  Lora: { group: 'Serif' },
  Merriweather: { group: 'Serif' },
  'Cormorant Garamond': { group: 'Serif' },
  'Crimson Text': { group: 'Serif' },
  Cinzel: { group: 'Serif' },
  // Sans
  Inter: { group: 'Sans' },
  Poppins: { group: 'Sans' },
  Montserrat: { group: 'Sans' },
  Karla: { group: 'Sans' },
  // Script
  'Dancing Script': { group: 'Script' },
  Pacifico: { group: 'Script' },
  'Great Vibes': { group: 'Script' },
  Caveat: { group: 'Script' },
  // Hand
  'Indie Flower': { group: 'Hand' },
  'Permanent Marker': { group: 'Hand' },
  'Rock Salt': { group: 'Hand' },
  'Amatic SC': { group: 'Hand' },
};

/**
 * The curated 24 (of ~154). Each survives `fontSanitation` / `menuThemeFontValue`,
 * which are keyed off the registry in `lib/types/menu.ts`.
 */
export const CURATED_FONTS: CuratedFont[] = fonts
  .filter((font) => CURATED[font.name])
  .map((font) => ({ name: font.name, css: font.css, group: CURATED[font.name].group }));

export const CUSTOM_CURATED_FONT_NAMES: ReadonlySet<string> = new Set(CURATED_FONTS.map((font) => font.name));

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const designOverridesSchema = z
  .object({
    colors: z
      .object({
        background: z.string().regex(HEX_COLOR).optional(),
        surface: z.string().regex(HEX_COLOR).optional(),
        primary: z.string().regex(HEX_COLOR).optional(),
        text: z.string().regex(HEX_COLOR).optional(),
        price: z.string().regex(HEX_COLOR).optional(),
        accent: z.string().regex(HEX_COLOR).optional(),
      })
      .partial()
      .strict()
      .optional(),
    fonts: z
      .object({
        heading: z.string().refine((value) => CUSTOM_CURATED_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        body: z.string().refine((value) => CUSTOM_CURATED_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        price: z.string().refine((value) => CUSTOM_CURATED_FONT_NAMES.has(value), 'Unknown font name.').optional(),
      })
      .partial()
      .strict()
      .optional(),
    skin: z.string().min(1).max(80).optional(),
  })
  .strict();

export type DesignOverrides = z.infer<typeof designOverridesSchema>;

/**
 * Rejects unknown keys even when the partial groups are empty: an empty body
 * `{}` stays valid (a no-op PATCH = reset), but `{ colors: { booty: "#000" } }`
 * must fail the slot ∈ enum contract from §4.4.
 */
export function isDesignOverrideReset(overrides: DesignOverrides): boolean {
  return !overrides.colors && !overrides.fonts && !overrides.skin;
}

// --- §4.3 Contrast guard ----------------------------------------------------

export function contrastRatio(foreground: string, background: string): number {
  return colordContrastRatio(foreground, background);
}

export function meetsAAContrast(foreground: string, background: string, largeText = false): boolean {
  return contrastRatio(foreground, background) >= (largeText ? 3 : 4.5);
}

export interface ContrastCheck {
  name: string;
  ratio: number;
  passes: boolean;
}

/**
 * Checks the pairs from §4.3 against the *effective* colours: base palette
 * (template + theme result) overlaid with the current override edits, so the
 * warning appears the moment a swatch change breaks its pair.
 */
export function checkDesignContrast(
  overrides: { colors?: Partial<Record<DesignColorKey, string>> },
  baseColors: Partial<Record<DesignColorKey, string>>,
): ContrastCheck[] {
  return DESIGN_CONTRAST_PAIRS.map((pair) => {
    const fg = overrides.colors?.[pair.fg] || baseColors[pair.fg];
    const bg = overrides.colors?.[pair.bg] || baseColors[pair.bg];
    if (!fg || !bg) return { name: pair.name, ratio: NaN, passes: true };
    const ratio = contrastRatio(fg, bg);
    return { name: pair.name, ratio, passes: ratio >= 4.5 };
  });
}

/**
 * "Fix for me" picks the stronger black/white text companion for the current
 * background when the merchant's chosen foreground misses normal-text AA.
 */
export function suggestContrastFix(foreground: string, background: string): string {
  const currentRatio = contrastRatio(foreground, background);
  if (currentRatio >= 4.5) return foreground;
  const blackRatio = contrastRatio('#000000', background);
  const whiteRatio = contrastRatio('#ffffff', background);
  return blackRatio >= whiteRatio ? '#000000' : '#ffffff';
}
